import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { defaultKunde, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    Lieferung,
    addLieferung,
    updateLieferung,
    recalculateLieferung,
    sendLieferung,
    copyLieferung,
    getDefaultLieferung,
    deleteLieferung,
    finalizeLieferung,
    markLieferungForExport,
    useLieferung,
} from 'api/lieferung';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { LieferungStatus } from 'formComponents/lookups/LieferungStatus';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, createRecordUrl, formatMoney } from 'utils/formatter';
import { useLieferungPositionList } from 'api/lieferungPosition';
import { AnsprechpartnerSelect } from 'components/Kunde/Ansprechpartner/AnsprechpartnerSelect';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { getApiUrl } from 'api/useApi';
import { SendMail } from 'components/SendMail/SendMail';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { ZusatzadresseSelect } from 'components/Kunde/Zusatzadresse/ZusatzadresseSelect';
import { Lieferantenformular } from 'formComponents/lookups/Lieferantenformular';
import { addRechnungFromLieferung } from 'api/rechnung';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { ZusatzadresseTyp } from 'api/zusatzadresse';
import { LieferungStatusLabel } from 'utils/labels';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { useHistory } from 'react-router-dom';
import { logDifference } from 'utils/helper';
import { SmRefType } from 'api/attachment';
import { AraRegelung } from 'formComponents/lookups/AraRegelung';
import { useSelector } from 'react-redux';
import { LieferungPositionen } from './LieferungPositionen';
import { AddAttachments } from '../History/AddAttachments';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';
import { authSelector } from '../../store/auth/selector';

export interface LieferungEditData {
    kNr?: number;
    lNr: string;
}

interface LieferungEditProps {
    defaultKNr?: number;
    lNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (aNr: string) => void;
}

type ButtonAction =
    | 'reopen'
    | 'print'
    | 'printOnBriefpapier'
    | 'send'
    | 'copy'
    | 'createRechnung'
    | 'finalize'
    | 'delete';

const allowedStatus = [0, 1, 4];

export const LieferungEdit: React.FC<LieferungEditProps> = ({ defaultKNr, lNr, onClose, onReopen }) => {
    const history = useHistory();
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [lieferung, setLieferung] = React.useState<Lieferung | null>(getDefaultLieferung(defaultKNr));
    const dbLieferung = useLieferung(
        {
            nr: lNr,
            withAuftrag: true,
        },
        {
            enabled: lNr !== 'neu',
            onSuccess: (loadedLieferung) => {
                setLieferung(cloneDeep(loadedLieferung));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: lieferung?.kNr,
            withAttachmentList: true,
        },
        {
            enabled: lieferung?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (lNr === 'neu' && lieferung) {
                    setLieferung({
                        ...lieferung,
                        betreuer: newKunde?.betreuer || 0,
                        zahlBed: newKunde?.zahlZiel || lieferung.zahlBed,
                    });
                }
            },
        },
    );

    const lieferungPositionList = useLieferungPositionList(
        {
            lNr,
        },
        { enabled: lNr !== 'neu' },
    );

    // const [kunde, setKunde] = React.useState<Kunde | null>(null);
    const [showCopy, setShowCopy] = React.useState(false);
    const [showSend, setShowSend] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    // const [showBook, setShowBook] = React.useState(false);
    const [showCreateRechnung, setShowCreateRechnung] = React.useState(false);
    const [showFinalize, setShowFinalize] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    /*
    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kundeList.data[0].nr !== kunde?.nr
        ) {
            setKunde(kundeList.data[0]);
            if (lNr === 'neu' && lieferung) {
                setLieferung({
                    ...lieferung,
                    betreuer: kundeList.data[0]?.betreuer || 0,
                    zahlBed: kundeList?.data[0]?.zahlZiel || lieferung.zahlBed,
                });
            }
        }
    }, [kunde, kundeList, lNr, lieferung]);

    React.useEffect(() => {
        if (!lieferungList.isLoading && !lieferungList.isFetching && lieferung === null) {
            if (lieferungList.data?.length === 1) {
                setLieferung(cloneDeep(lieferungList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setLieferung(getDefaultLieferung(defaultKNr));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [lieferung, lieferungList, defaultKNr, auth, kundeList]);
     */

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbLieferung.isLoading &&
            !dbLieferung.isFetching &&
            lieferung !== null
        ) {
            if (!isEqual(dbLieferung.data, lieferung)) {
                setSavingState(SavingState.notSaved);
                logDifference('lieferung', lieferung, dbLieferung.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbLieferung, lieferung]);

    const readOnly = React.useMemo(() => {
        return lieferung?.status !== 0;
    }, [lieferung?.status]);

    const validate = React.useCallback((): boolean => {
        if (lieferung?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Kunden aus.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [lieferung, validationError]);

    const handleButtonAction = React.useCallback(
        (action?: ButtonAction) => {
            if (!lieferung?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(lieferung.nr);
                    break;
                case 'print':
                    window.open(getApiUrl('lieferung/pdf') + '?nr=' + lieferung.nr);
                    break;
                case 'printOnBriefpapier':
                    window.open(getApiUrl('lieferung/pdf') + '?nr=' + lieferung.nr + '&briefpapier=true');
                    break;
                case 'send':
                    setShowSend(true);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                case 'createRechnung':
                    setShowCreateRechnung(true);
                    break;
                case 'finalize':
                    setShowFinalize(true);
                    break;
                default:
                    break;
            }
        },
        [lieferung, onReopen],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number) => {
            if (!lieferung) {
                return;
            }

            const updatedLieferung: Lieferung = status === undefined ? lieferung : { ...lieferung, status };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (lNr === 'neu') {
                        addLieferung(updatedLieferung)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateLieferung(updatedLieferung)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbLieferung.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(lieferung.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [dbLieferung, lieferung, lNr, isMounted, onReopen, savingState, validate],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: LieferungStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [
            {
                key: 'print',
                text: 'Drucken mit Briefkopf',
                iconProps: { iconName: 'Print' },
                onClick: () => handleButtonAction('print'),
            },
        ];

        if (lNr !== 'neu' && lieferung?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => handleButtonAction('delete'),
            });
        }

        return { items };
    }, [lNr, lieferung?.status, handleButtonAction]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Lieferung, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(lieferung?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Lieferung, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={lieferung?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    /*
    const renderCheckbox = (column: keyof Lieferung, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={lieferung?.[column] as boolean}
            onChange={(event, checked) => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );
     */

    const renderMa = (column: keyof Lieferung, label: string) => (
        <BetreuerById
            value={lieferung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty
            menuPlacement="top"
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        [column]: newValue,
                    });
                }
            }}
        />
    );

    const renderTextbaustein = (
        column: keyof Lieferung,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={lieferung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (column: keyof Lieferung, label: string, required = false, defaultNow = false) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(lieferung?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (lieferung) {
                        setLieferung({
                            ...lieferung,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: readOnly,
                }}
            />
        );
    };

    const renderLieferungStatus = () => (
        <LieferungStatus
            value={lieferung?.status?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            label="Kunde"
            kNr={lieferung?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly || lNr !== 'neu'}
            onChange={(newKNr): void => {
                if (lieferung && lieferung.kNr !== newKNr) {
                    setLieferung({
                        ...lieferung,
                        kNr: newKNr,
                        kaNr: 0,
                        kaName: '',
                        lieferAdrNr: 0,
                    });
                }
            }}
        />
    );

    const renderAnsprechpartner = () => (
        <AnsprechpartnerSelect
            label="Ansprechpartner"
            kNr={lieferung?.kNr}
            kaNr={lieferung?.kaNr}
            kaName={lieferung?.kaName}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newKaNr, newKaName): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        kaNr: newKaNr || 0,
                        kaName: newKaName || '',
                    });
                }
            }}
        />
    );

    const renderZusatzadresse = (
        column: 'lieferAdrNr',
        subRecord: 'lieferAdr',
        label: string,
        typ: ZusatzadresseTyp,
    ) => (
        <ZusatzadresseSelect
            label={label}
            kNr={lieferung?.kNr}
            zaNr={lieferung?.[column] || 0}
            typ={typ}
            zusatzadresse={lieferung?.[subRecord]}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newZaNr, newZusatzadresse): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        [column]: newZaNr || 0,
                        [subRecord]: newZusatzadresse,
                    });
                }
            }}
        />
    );

    const renderSkontodefinition = () => (
        <Skontodefinition
            value={lieferung?.zahlBed || 0}
            label="Zahlungsbedingungen"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        zahlBed: newValue,
                    });
                }
            }}
        />
    );

    const renderLieferantenformular = () => (
        <Lieferantenformular
            value={lieferung?.formular || 0}
            label="Lieferschein"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        formular: newValue,
                    });
                }
            }}
        />
    );

    const renderAraRegelung = () => (
        <AraRegelung
            value={lieferung?.ara || 0}
            label="ARA Regelung"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (lieferung) {
                    setLieferung({
                        ...lieferung,
                        ara: newValue,
                    });
                }
            }}
        />
    );

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={`Lieferung ${lieferung?.nr || ''} - ${kunde.data?.name1 || '...'} ${kunde.data?.name2 || ''} ${
                    kunde.data?.nr ? '(' + kunde.data.nr + ')' : ''
                }`}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde ? <OpenLinkIcon link={createKundeUrl(kunde.data?.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell}>{renderAnsprechpartner()}</div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Lieferschein-Nr.', { maxLength: 10, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('auNr', 'Auftrag-Nr.', { maxLength: 10 })}
                                    {lieferung?.auNr ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('auftraege', defaultKNr, lieferung?.auNr)}
                                        />
                                    ) : null}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Ausstellungsdatum', true, true)}
                                </div>
                                {/*
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datBis', 'Auftrag gültig bis')}
                                </div>
                                */}
                            </div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderMa('betreuer', 'Betreuer')}</div>
                                {/*
                                <div className={formStyles.checkboxWithoutLabel}>
                                    {renderCheckbox('rahmen', 'Rahmenauftrag')}
                                </div>
                                */}
                            </div>

                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && lNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.lieferung}
                                        smRef={lNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={formStyles.secondColumn}>
                            <div className={formStyles.gridCell}>{renderTextarea('txt', 'Text')}</div>
                            {/*
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB1', 'Textbaustein 1', 24, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB2', 'Textbaustein 2', 24, true)}
                            </div>
                            */}
                            <div className={formStyles.gridCell}>
                                {renderZusatzadresse('lieferAdrNr', 'lieferAdr', 'Lieferadresse', 'L')}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderLieferungStatus()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefBed', 'Lieferbedingung', 11, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefZeit', 'Lieferzeit', 14, true)}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderDatePicker('liefDat', 'Lieferdatum')}</div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('liefKw', 'Liefer-KW', { numeric: true })}
                                </div>
                            </div>
                            <div className={formStyles.gridCell}>{renderSkontodefinition()}</div>
                            <div className={formStyles.gridCell}>{renderAraRegelung()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('aktText', 'Betreff E-Akt', { maxLength: 100 })}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('mwstCode', 'MwSt.', { numeric: true, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>{renderLieferantenformular()}</div>
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Kunden)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Kunden)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={lieferung?.auftrag?.zusatz || ''}
                                label="Bemerkung (beim Auftrag)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                    </div>

                    <div>
                        {lieferung && lieferungPositionList?.data && (
                            <LieferungPositionen
                                kunde={kunde.data || defaultKunde}
                                lieferung={lieferung}
                                lieferungPositionen={lieferungPositionList?.data}
                                allowEdit={!readOnly && savingState === SavingState.notChanged}
                                viewMode="long"
                                refetch={async () => {
                                    const result = await recalculateLieferung(lieferung);
                                    if (result?.lieferung) {
                                        dbLieferung.refetch().then();
                                        setLieferung({
                                            ...lieferung,
                                            sNetto: result.lieferung.sNetto,
                                            sBrutto: result.lieferung.sBrutto,
                                            sMwst: result.lieferung.sMwst,
                                        });
                                        lieferungPositionList.refetch().then();
                                    }

                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {lieferungPositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(lieferung?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(lieferung?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(lieferung?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={lieferung?.anlageDat}
                        createdDecimalTime={lieferung?.anlageUhr}
                        createdUser={lieferung?.anlageUser}
                        modifiedDate={lieferung?.updDat}
                        modifiedDecimalTime={lieferung?.updUhr}
                        modifiedUser={lieferung?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !lieferung?.nr || auth.rolle === 'EX'}
                        />
                        <DefaultButton
                            className={formStyles.sendButton}
                            onClick={() => handleButtonAction('send')}
                            text="Senden"
                            disabled={savingState !== SavingState.notChanged || !lieferung?.nr || auth.rolle === 'EX'}
                        />
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => handleButtonAction('createRechnung')}
                            text="Rechnung erstellen"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                !lieferung?.nr ||
                                lieferung?.status === 4 ||
                                auth.rolle === 'EX'
                            }
                        />
                        <DefaultButton
                            className={formStyles.finalizeButton}
                            onClick={() => handleButtonAction('finalize')}
                            text="Lieferung abschließen"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                !lieferung?.nr ||
                                lieferung?.status === 4 ||
                                auth.rolle === 'EX'
                            }
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !lieferung?.nr || auth.rolle === 'EX'}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !lieferung?.nr}
                            onClick={() => handleButtonAction('printOnBriefpapier')}
                            text="Drucken"
                            menuProps={actionButtonMenuProps}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={
                                    savingState === SavingState.notChanged ||
                                    dbLieferung.isLoading ||
                                    auth.rolle === 'EX'
                                }
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbLieferung.isLoading ||
                    dbLieferung.isFetching ||
                    kunde.isLoading ||
                    kunde.isFetching ||
                    !lieferung) && <LoadingIndicator />}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Kunde"
                    disabled={savingState === SavingState.saving || readOnly || lNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={lieferung?.kNr}
                    onChange={async (newKNr) => {
                        if (lieferung && newKNr) {
                            const { newNr } = await copyLieferung(newKNr, lieferung, lieferungPositionList?.data);
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showSend && (
                <SendMail
                    kNr={lieferung?.kNr || 0}
                    kaNr={undefined}
                    markAsSentDisabled={lieferung?.status !== 0}
                    markAsSentLabel="Lieferung als versendet markieren"
                    markForExportDisabled={false}
                    markForExportLabel="Lieferung für CSV Export vormerken"
                    onSubmit={(kaNrListTo, kaNrListCC, markAsSent, markForExport) => {
                        if (lieferung) {
                            sendLieferung(lieferung, kaNrListTo, kaNrListCC).then(() => {
                                setShowSend(false);
                                if (markAsSent && lieferung) {
                                    save(undefined, 1);
                                }
                                if (markForExport && lieferung) {
                                    markLieferungForExport(lieferung).then();
                                }
                            });
                        }
                    }}
                    onCancel={() => setShowSend(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Lieferung löschen"
                    message={'Soll der Lieferung ' + lieferung?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (lieferung?.nr) {
                            deleteLieferung(lieferung).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => setShowDelete(false)}
                />
            )}
            {showCreateRechnung && (
                <ConfirmMessage
                    title="Rechnung erstellen"
                    message={'Soll aus der Lieferung ' + lieferung?.nr + ' eine Rechnung erstellt werden?'}
                    minWidth={550}
                    content={
                        <ul>
                            <li>Ein Rechnung mit sämtlichen Positionen aus der Lieferung wird erstellt.</li>
                            <li>Die Auftragsnummer aus der Lieferung wird bei der Rechnung hinterlegt.</li>
                            <li>Der Lieferschein wird abgeschlossen.</li>
                            <li>Die neue Rechnung wird geöffnet.</li>
                        </ul>
                    }
                    onConfirm={async () => {
                        if (kunde.data && lieferung?.nr) {
                            await addRechnungFromLieferung(kunde.data, lieferung, lieferungPositionList?.data).then(
                                async (result) => {
                                    await finalizeLieferung(lieferung).then();
                                    history.push(createRecordUrl('rechnungen', lieferung.kNr, result.newNr));
                                    /*
                                    save(() => {
                                        history.push(createRecordUrl('rechnungen', lieferung.kNr, result.newNr));
                                    }, 4);
                                     */
                                },
                            );
                        }
                    }}
                    onCancel={() => setShowCreateRechnung(false)}
                />
            )}
            {showFinalize && (
                <ConfirmMessage
                    title="Lieferung abschließen"
                    message={'Soll die Lieferung ' + lieferung?.nr + ' abgeschlossen werden?'}
                    minWidth={550}
                    content={
                        <ul>
                            <li>
                                Bei Lieferart <strong>Lager</strong> wird <strong>Offene Lieferungen</strong> in der
                                Lagerverwaltung wird reduziert.
                            </li>
                            <li>
                                Der Status der Lieferung wird auf <strong>{LieferungStatusLabel[4]}</strong> gesetzt.
                            </li>
                        </ul>
                    }
                    onConfirm={async () => {
                        if (kunde && lieferung?.nr) {
                            await finalizeLieferung(lieferung).then(() => {
                                onReopen(lieferung.nr);
                            });
                        }
                    }}
                    onCancel={() => setShowFinalize(false)}
                />
            )}
            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    message={
                        'Soll der Status von Lieferung ' +
                        lieferung?.nr +
                        ' auf "' +
                        LieferungStatusLabel[changeStatusValue] +
                        '" geändert werden?'
                    }
                    onConfirm={() => {
                        if (lieferung?.nr) {
                            save(() => setChangeStatusValue(null), changeStatusValue);
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && lNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.lieferung}
                    forcedSmRef={lNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
